import * as React from 'react';

import { IBasicStyledProps } from '@notino/react-styleguide';

import { HorizontalChevron } from '@sharedComponents/Icons/HorizontalChevron';

import {
  DropdownContainer,
  MobileNativeSelect,
  Option,
  OptionsList,
  Selected,
} from './styled';

interface DropdownProps<T> extends IBasicStyledProps {
  options: T[];
  selectedKey: string;
  onSelect?: (value: T) => void;
  renderSelected: (value: T, isOpen: boolean) => React.ReactNode;
  renderOption: (value: T, isSelected: boolean) => React.ReactNode;
  mobileNativeSelectConfig?: {
    getLabel: (value: T) => string;
  };
}

function Dropdown<T extends { key: string }>({
  options,
  selectedKey,
  onSelect,
  renderSelected,
  renderOption,
  mobileNativeSelectConfig,
  ...basicStyleProps
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    const abortController = new AbortController();
    document.addEventListener(
      'mousedown',
      (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      },
      { signal: abortController.signal }
    );
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <DropdownContainer {...basicStyleProps} ref={dropdownRef}>
      <Selected onClick={() => setIsOpen(!isOpen)} data-id="selected-value">
        {renderSelected(
          options.find((option) => option.key === selectedKey),
          isOpen
        )}
        <HorizontalChevron
          isOpened={isOpen}
          width="1rem"
          height="1rem"
          color="icon.tertiary"
        />
      </Selected>

      {isOpen && (
        <OptionsList data-id="options-list">
          {options.map((option) => (
            <Option
              key={option.key}
              onClick={() => {
                onSelect?.(option);
                setIsOpen(false);
              }}
            >
              {renderOption(option, option.key === selectedKey)}
            </Option>
          ))}
        </OptionsList>
      )}

      {mobileNativeSelectConfig && (
        <MobileNativeSelect
          onChange={(e) => {
            onSelect?.(options.find((option) => option.key === e.target.value));
          }}
          value={selectedKey}
        >
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {mobileNativeSelectConfig.getLabel(option)}
            </option>
          ))}
        </MobileNativeSelect>
      )}
    </DropdownContainer>
  );
}

export default Dropdown;
